import {HttpClient}                                                                                        from '@angular/common/http';
import {Component}                                                                                         from '@angular/core';
import {FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {catchError, firstValueFrom, of} from 'rxjs';
import {MatSnackBar}                                                                                       from '@angular/material/snack-bar';
import {ActivatedRoute}                                                                                    from '@angular/router';
import {User}                                                                                              from '@src/app/admin/users/models/user.model';
import {UsersService}                                                                                      from '@src/app/admin/users/services/users.service';
import {AuthService}                                                                                       from '@src/app/core/services/auth.service';
import {ApiService}                                                                                        from '@src/app/shared/services/api.service';
import {WalkthroughService}                                                                                from '@src/app/shared/services/walkthrough/walkthrough.service';
import {AddressType}                                                                                       from '../../models/addresses.model';
import {OrganisationService}                                                                               from '../../services/organisation.service';

@Component({
    standalone: false,
    selector: 'app-organisation-settings',
    templateUrl: './organisation-settings.component.html',
    styleUrls: ['./organisation-settings.component.scss']
})
export class OrganisationSettingsComponent
{
    public organisationForm: FormGroup;
    public organisationReference = '';
    public submitted: boolean = false;
    public saving = false;
    public systemUsers: User;
    public imagePreview: any = null;
    public add_system_owners = [];
    public remove_system_owners = [];
    public countries = [];
    public tradingCountrySearch = [];
    public billingCountrySearch = [];
    public defaultCountry = {name: {common: 'United Kingdom'}, cca2: 'GB', flags: {png: 'https://flagcdn.com/w320/gb.png'}};
    private previousBillingAddress: any = null;

    get f()
    {
        return this.organisationForm.controls;
    }

    get system_owners()
    {
        return this.f['system_owners'] as UntypedFormArray;
    }

    constructor(
        public walkthroughService: WalkthroughService,
        private fb: UntypedFormBuilder,
        private userService: UsersService,
        private organisationService: OrganisationService,
        private authService: AuthService,
        private snackBar: MatSnackBar,
        private apiService: ApiService,
        private activatedRoute: ActivatedRoute,
        private httpClient: HttpClient
    )
    {
        this.userService.list(1).subscribe(response => {
            this.systemUsers = response.data;
        }, error => this.userService.ErrorHandler(error));

        this.walkthroughService.start('settings.company', false);

        this.activatedRoute.data.subscribe(
            response => {
                let responseData = response.organisation.data;
                this.organisationReference = responseData.id ?? '';
                this.imagePreview = responseData.logo_url ?? null;

                this.organisationForm = new UntypedFormGroup({
                    name: new UntypedFormControl(responseData.name ?? null, Validators.required),
                    billing_email: new UntypedFormControl(responseData.billing_email ?? null, [Validators.required, Validators.email]),
                    logo: new UntypedFormControl(null, Validators.required),
                    system_owners: new UntypedFormArray([], Validators.required),
                    add_system_owner: new UntypedFormControl(null),
                    trading_address: this.addressForm(responseData.trading_address, AddressType.TradingAddress),
                    billing_address: this.addressForm(responseData.billing_address, AddressType.BillingAddress),
                });

                const addressControls = [this.f.trading_address, this.f.billing_address];
                this.countries = Array.from(new Map([this.defaultCountry, ...addressControls.map(control => control.value.country)].map(country => [country.cca2, country])).values());
                this.tradingCountrySearch = this.billingCountrySearch = this.countries;
                this.httpClient.get<any[]>('https://restcountries.com/v3.1/all?fields=name,flags,cca2').pipe(
                    catchError(() => of([]))
                ).subscribe(countries => {
                    this.countries = Array.from(new Map([...this.countries, ...countries].map(country => [country.cca2, country])).values());
                    for (const control of addressControls) {
                        control.get('country').setValue(this.countries.find(country => country.cca2 === control.value.country.cca2));
                    }
                    this.tradingCountrySearch = this.billingCountrySearch = this.countries;
                });

                for (let owner of responseData.system_owners) {
                    this.addSystemOwner(false, owner);
                }

                for (let [index, owner] of this.system_owners.getRawValue().entries()) {
                    if (owner.id == this.userService.user.id || this.system_owners.getRawValue().length == 1) {
                        this.system_owners.controls[index].disable();
                    }
                }
            },
            error => {
                this.apiService.handleError(error);
            }
        );
    }

    private addressForm(address: any, type: AddressType): UntypedFormGroup
    {
        const country = address ? {name: {common: address.country}, cca2: address.country_code, flags: {png: ''}} : this.defaultCountry;
        return this.fb.group({
            address_id: [address?.id ?? null],
            type_id: [type],
            title: [type === AddressType.TradingAddress ? 'Trading Address' : 'Billing Address'],
            address_line_1: [address?.address_line_1 ?? null, Validators.required],
            address_line_2: [address?.address_line_2 ?? null],
            address_line_3: [address?.address_line_3 ?? null],
            locality: [address?.locality ?? null],
            county: [address?.county ?? null],
            country: [country, Validators.required],
            postcode: [address?.postcode ?? null, Validators.required],
        });
    }

    public async submitForm(): Promise<void>
    {
        if (this.saving) {
            return;
        }
        this.submitted = true;
        const addresses = [this.f.billing_address, this.f.trading_address].filter(control => {
            const address = control.value;
            return address.address_id || ['address_line_1', 'address_line_2', 'address_line_3', 'locality', 'county', 'postcode'].some(field => !!address[field]);
        });
        if (this.f.name.invalid || this.f.billing_email.invalid || addresses.some(control => control.invalid)) {
            this.organisationForm.markAllAsTouched();
            this.snackBar.open('Please complete the details you have started.', 'Ok.');
            return;
        }

        const formData = new FormData();
        formData.append('name', this.f.name.value);
        formData.append('billing_email', this.f.billing_email.value);
        if (!(typeof this.f.logo.value === 'string') && this.f.logo.value !== null) {
            formData.append('logo', this.f.logo.value);
        }
        for (const owner of this.add_system_owners.map(so => so?.id ?? so)) {
            formData.append('add_system_owner_ids[]', owner);
        }
        for (const owner of this.remove_system_owners.map(so => so?.id ?? so)) {
            formData.append('remove_system_owner_ids[]', owner);
        }

        this.saving = true;
        try {
            await firstValueFrom(this.organisationService.editOrganisation(this.authService.tenant?.id, formData));
            for (const control of addresses) {
                const address = control.value;
                const request = address.address_id
                    ? this.organisationService.editAddress(address.address_id, address)
                    : this.organisationService.createAddress(address);
                const response = await firstValueFrom(request);
                control.get('address_id').setValue(response.data.id);
            }
            this.snackBar.open('Organisation settings saved successfully.', 'Ok.');
        } catch (error) {
            this.snackBar.open('There was a problem saving the settings. Please try again.', 'Ok.');
        } finally {
            this.saving = false;
        }
    }

    public onDragDropFileChange(files: File[]): void
    {
        const mockEvent = {target: {files}} as unknown as Event;
        this.onFileChange(mockEvent);
    }

    public onFileChange(event: Event): void
    {
        const input = event.target as HTMLInputElement;

        this.f.logo.setValue(input.files[0]);

        let reader = new FileReader();

        reader.onload = (event: any) => {
            this.imagePreview = event.target.result;
        };

        reader.readAsDataURL(input.files[0]);
    }

    public removeFile(): void
    {
        this.f.logo.setValue(null);
        this.imagePreview = null;
    }

    public sameAsTradingAddress(event: any): void
    {
        if (event.checked) {
            this.previousBillingAddress = {...this.f.billing_address.value};
            const {address_id, type_id, title, ...addressFields} = this.f.trading_address.value;
            this.f.billing_address.patchValue(addressFields);
        } else if (this.previousBillingAddress) {
            this.f.billing_address.patchValue(this.previousBillingAddress);
            this.previousBillingAddress = null;
        }
    }

    public addSystemOwner(newOwner: boolean = false, owner_id?): void
    {
        const system_owner = this.fb.group({
            id: [owner_id ?? this.organisationForm.controls.add_system_owner.value, Validators.required],
            name: ['beginner', Validators.required]
        });

        this.system_owners.push(system_owner);
        if (newOwner) {
            this.add_system_owners.push(owner_id ?? this.organisationForm.controls.add_system_owner.value);
            this.organisationForm.controls.add_system_owner.setValue(null);
        }
    }

    public removeSystemOwner(ownerIndex: number): void
    {
        let ownerVal = this.system_owners.controls[ownerIndex].value.id;

        this.system_owners.removeAt(ownerIndex);

        let index = this.add_system_owners.findIndex(c => c == ownerVal);
        index >= 0 ? this.add_system_owners.splice(index, 1) : this.remove_system_owners.push(ownerVal);
    }

    public forceUppercaseConditionally(formGroup, formControlName, event)
    {
        this.organisationForm.get(`${formGroup}.${formControlName}`).setValue(event.target.value.toUpperCase());
    }

    public searchTradingCountries(event: Event): void
    {
        const input = event.target as HTMLInputElement;
        input.value != '' ? this.tradingCountrySearch = this.countries.filter(c => c.name.common.toLowerCase().includes(input.value.toLowerCase())) : this.tradingCountrySearch = this.countries;
    }

    public searchBillingCountries(event: Event): void
    {
        const input = event.target as HTMLInputElement;
        input.value != '' ? this.billingCountrySearch = this.countries.filter(c => c.name.common.toLowerCase().includes(input.value.toLowerCase())) : this.billingCountrySearch = this.countries;
    }
}
